import { getPublished } from '@/data/loader';
import { useI18n } from '@/hooks/useI18n';
import { useTranslation } from '@/hooks/useTranslation';
import { EntityCard } from '@/components/EntityCard';
import { ExperienciasSEO } from '@/components/SEO';
import { Container } from '@/components/Container';
import { whatsappClick } from '@/lib/analytics';
import { OptimizedImage } from '@/components/OptimizedImage';

export function ExperienciasPage() {
  const { lang } = useI18n();
  const { t } = useTranslation();
  const passeiosList = getPublished('passeio', lang);
  const restaurantesList = getPublished('restaurante', lang);

  return (
    <>
      <ExperienciasSEO />
      <div>
        {/* Hero */}
        <div className="relative mb-10">
          <div className="relative h-56 sm:h-64">
            <OptimizedImage
              src="/images/sailboat.jpg"
              alt={t('experiences.heroImageAlt')}
              className="w-full h-full object-cover"
              priority
            />
            <div className="absolute inset-0 hero-gradient" />
          </div>
          <div className="absolute inset-0 flex items-center justify-center text-center px-4">
            <div>
              <h1 className="font-display text-5xl sm:text-6xl text-white mb-2">
                {t('experiences.title')}
              </h1>
              <p className="font-editorial text-xl text-white/80">{t('experiences.subtitle')}</p>
            </div>
          </div>
        </div>

        {/* Passeios */}
        <Container>
          <section className="mb-16">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-body text-sm text-coral font-semibold uppercase tracking-wider">
                {t('experiences.toursLabel')}
              </span>
            </div>
            <h2 className="font-display text-4xl text-warm-dark mb-2">
              {t('experiences.toursTitle')}
            </h2>
            <p className="font-editorial text-warm-gray mb-8">{t('experiences.toursSubtitle')}</p>

            <div className="grid md:grid-cols-2 gap-6">
              {passeiosList.map((p: any) => (
                <div
                  key={p.id}
                  className="bg-white rounded-2xl border border-sand-200 shadow-[0_4px_16px_rgba(0,0,0,0.06)] overflow-hidden"
                >
                  <div className="relative h-48">
                    <img
                      src={
                        p.handle === 'batismo-vela'
                          ? '/images/sailboat.jpg'
                          : '/images/trilha-floresta.jpg'
                      }
                      alt={p.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                    {p.provider && (
                      <span className="absolute top-4 right-4 bg-coral text-white text-xs font-body font-bold px-3 py-1 rounded-full">
                        {t('experiences.partnerBadge')}
                      </span>
                    )}
                    <div className="absolute bottom-4 left-4 text-white">
                      <h3 className="font-display text-2xl">{p.title.toUpperCase()}</h3>
                      <p className="font-editorial text-sm text-white/80">{p.subtitle}</p>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="space-y-2 mb-5">
                      {p.itinerary?.slice(0, 4).map((item: any, i: number) => (
                        <div key={i} className="flex items-center gap-3 text-sm">
                          <span className="font-body text-teal-deep font-mono text-xs w-10 flex-shrink-0">
                            {item.time}
                          </span>
                          <span className="font-body text-warm-gray">{item.activity}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between pt-4 border-t border-sand-200">
                      <div>
                        {p.price?.amount != null ? (
                          <>
                            <span className="font-display text-2xl text-coral">
                              R$ {p.price.amount}
                            </span>
                            <span className="font-body text-sm text-warm-gray ml-1">
                              /{p.price?.unit}
                            </span>
                          </>
                        ) : (
                          <span className="font-display text-lg text-coral">Sob consulta</span>
                        )}
                      </div>
                      {p.provider && (
                        <a
                          href={`https://wa.me/55${p.provider.phone.replace(/\D/g, '')}?text=${encodeURIComponent(p.provider.whatsappMessage + '\n\n(via vemprailhadomel.com.br — experiencias)')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => whatsappClick(p.provider.name, 'experiencias')}
                          className="inline-flex items-center gap-2 bg-coral hover:bg-coral-dark text-white font-body font-semibold rounded-xl px-5 py-2.5 transition-all"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                          </svg>
                          {t('experiences.bookButton')}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Restaurantes */}
          <section>
            <div className="flex items-center gap-2 mb-2">
              <span className="font-body text-sm text-coral font-semibold uppercase tracking-wider">
                {t('experiences.foodLabel')}
              </span>
            </div>
            <h2 className="font-display text-4xl text-warm-dark mb-2">
              {t('experiences.foodTitle')}
            </h2>
            <p className="font-editorial text-warm-gray mb-8">{t('experiences.foodSubtitle')}</p>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {restaurantesList.map((entity: any) => (
                <EntityCard key={entity.handle} entity={entity} category="restaurantes" />
              ))}
            </div>
          </section>
        </Container>
      </div>
    </>
  );
}
